import { Outlet, createFileRoute, redirect } from '@tanstack/react-router';
import { useEffect, useLayoutEffect } from 'react';
import { z } from 'zod';

import {
  resolveChannelSignIn,
  safeChannelSignInNext,
} from '@/features/channel-dashboard/channel-sign-in';
import { useSettingsHashRedirect } from '@/features/settings/useSettingsHashRedirect';
import { subscriptionApis } from '@/features/subscription';
import { queryClient } from '@/query/client';
import { userQueryOptions } from '@/query/user';
import { uiStore } from '@/stores/ui.store';
import { userStore } from '@/stores/user.store';

const ChannelsRouteLayout = () => {
  useSettingsHashRedirect();

  useLayoutEffect(() => {
    document.body.classList.add('glass-theme');
    uiStore.setForcedTheme('light');
    return () => {
      document.body.classList.remove('glass-theme');
      uiStore.setForcedTheme(null);
    };
  }, []);

  useEffect(() => {
    void subscriptionApis.getStatus();
    void subscriptionApis.getPricingPlans();
  }, []);

  return <Outlet />;
};

export const Route = createFileRoute('/_channels')({
  component: ChannelsRouteLayout,
  staticData: { requiresAuth: true },
  validateSearch: z
    .object({ lb_sign_in: z.string().optional().catch(undefined) })
    .passthrough().parse,
  beforeLoad: async ({ context, location, search }) => {
    const signInCode = search.lb_sign_in;
    if (typeof signInCode === 'string') {
      const nextSearch = new URLSearchParams(location.searchStr);
      nextSearch.delete('lb_sign_in');
      const query = nextSearch.toString();
      const next = safeChannelSignInNext(
        `${location.pathname}${query ? `?${query}` : ''}${location.hash ? `#${location.hash}` : ''}`
      );
      const decision = await resolveChannelSignIn(signInCode, next);
      if (decision.kind === 'confirm') {
        throw redirect({
          to: '/channels/sign-in',
          search: { code: decision.code, next: decision.next },
          replace: true,
        });
      }
      const cleanedSearch = { ...search };
      delete cleanedSearch.lb_sign_in;
      throw redirect({
        to: location.pathname,
        search: cleanedSearch,
        hash: location.hash,
        replace: true,
      });
    }
    await context.auth.requireAuth(location);
  },
  loader: async () => {
    if (userStore.user) return;
    const user = await queryClient.ensureQueryData(userQueryOptions());
    userStore.setUser(user);
  },
});
